import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit, Output,
  ViewChild
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'lm-horizontal-scroller',
  templateUrl: 'lm-horizontal-scroller.component.html',
  styleUrls: [ 'lm-horizontal-scroller.component.scss' ],
  animations: [
    trigger('scroll', [
      state('next', style({
        transform: 'translateX({{scrollAmount}})'
      }), { params: { scrollAmount: '0', duration: '1s' } }),
      state('next2', style({
        transform: 'translateX({{scrollAmount}})'
      }), { params: { scrollAmount: '0', duration: '1s' } }),
      state('previous', style({
        transform: 'translateX({{scrollAmount}})'
      }), { params: { scrollAmount: '0', duration: '1s' } }),
      state('previous2', style({
        transform: 'translateX({{scrollAmount}})'
      }), { params: { scrollAmount: '0', duration: '1s' } }),

      // Need two instances of next/previous state.
      // Otherwise animation change detection fails to fire when coming from same state
      // next => next would fail to properly animate for example.
      transition('* => next', animate('{{duration}}s ease')),
      transition('* => next2', animate('{{duration}}s ease')),
      transition('* => previous', animate('{{duration}}s ease')),
      transition('* => previous2', animate('{{duration}}s ease')),

      transition('next => *', animate('{{duration}}s ease')),
      transition('next2 => *', animate('{{duration}}s ease')),
      transition('previous => *', animate('{{duration}}s ease')),
      transition('previous2 => *', animate('{{duration}}s ease')),

      transition('next => next2', animate('{{duration}}s ease')),
      transition('next2 => next', animate('{{duration}}s ease')),
      transition('previous => previous2', animate('{{duration}}s ease')),
      transition('previous2 => previous', animate('{{duration}}s ease')),

      transition('next => previous', animate('{{duration}}s ease')),
      transition('next => previous2', animate('{{duration}}s ease')),
      transition('next2 => previous', animate('{{duration}}s ease')),
      transition('next2 => previous2', animate('{{duration}}s ease')),

      transition('previous => next', animate('{{duration}}s ease')),
      transition('previous => next2', animate('{{duration}}s ease')),
      transition('previous2 => next', animate('{{duration}}s ease')),
      transition('previous2 => next2', animate('{{duration}}s ease')),
    ])
  ]
})
export class LMHorizontalScrollerComponent implements OnInit, AfterViewInit {
  // Amount gallery container will scroll left or right in px
  @Input() public scrollAmount = 320;

  // Time per animation in seconds
  @Input() public duration = .4;

  // Used to help keep track of scroll position
  @Input() itemCount: number;

  // Inner container, used to move content left/right via translateX
  @ViewChild('HorizontalScrollerContainer') scrollerContainer: ElementRef;

  @HostListener('window:resize', [ '$event' ]) onResize() {
    this.updateState();
  }

  @HostListener('(@scroll.start), [$event]') animationStart(e) {
    this.isAnimating = true;
  }

  @HostListener('(@scroll.done), [$event]')  animationDone(e) {
    this.isAnimating = false;
    this.updateState();
  }

  // Check if gallery container is larger than host container
  @Output() overflowed = new EventEmitter<boolean>();

  // Check if scroller is at start
  @Output() scrollIsStartEvent = new EventEmitter<boolean>();

  // Check if scroller is at end
  @Output() scrollIsEndEvent = new EventEmitter<boolean>();

  // Animation finished
  @Output() animationFinished = new EventEmitter<boolean>();

  public scrolling: boolean;

  private scrollPosition = 0;
  private touchStart: number;
  private touchEnd: number;
  private _translateXPosition: string;
  private _animationState: string;
  private _isAnimating = false;
  private _scrollIsStart: boolean;
  private _scrollIsEnd: boolean;
  private scrollWidth: number;

  // 0  = start
  // -x = scroll left position
  // +x = scroll right position
  private position = 0;

  // Host of gallery container
  private readonly hostElement:  HTMLElement;

  constructor(private elementRef: ElementRef) {
    this.hostElement = elementRef.nativeElement;
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this.scrollWidth = this.scrollerContainer.nativeElement.scrollWidth;
  }

  public scrollNext(): void {
    this.position++;
    this.setCurrentScrollAmount();
    this.animationState = this.animationState === 'next' ? 'next2' : 'next';
  }

  public scrollPrevious(): void {
    this.position--;
    this.setCurrentScrollAmount(true);
    this.animationState = this.animationState === 'previous' ? 'previous2' : 'previous';
  }

  public set translateXPosition(currentScrollAmount: string) {
    this._translateXPosition = currentScrollAmount;
  }

  public get translateXPosition(): string {
    return this._translateXPosition;
  }

  public set animationState(condition: string) {
    this._animationState = condition;
  }

  public get animationState() {
    return this._animationState;
  }

  public hasOverflow(): boolean {
    if (this?.scrollerContainer?.nativeElement?.parentElement) {
      return !(this.scrollerContainer.nativeElement.parentElement.scrollWidth <= this.scrollerContainer.nativeElement.parentElement.clientWidth);
    }

    return false;
  }

  public set isAnimating(isAnimating: boolean) {
    this._isAnimating = isAnimating;
  }

  public get isAnimating(): boolean {
    return this._isAnimating;
  }

  public set scrollIsStart(scrollIsStart: boolean) {
    this._scrollIsStart = scrollIsStart;
  }

  public get scrollIsStart(): boolean {
    return this._scrollIsStart;
  }

  public async scrollIsStartPromise(): Promise<boolean> {
    return await this.isStartPromise();
  }

  public async scrollIsEndPromise(): Promise<boolean> {
    return await this.isEndPromise();
  }

  public set scrollIsEnd(scrollIsEnd: boolean) {
    this._scrollIsEnd = scrollIsEnd;
  }

  public get scrollIsEnd(): boolean {
    return this._scrollIsEnd;
  }

  public onTouchStart(event: TouchEvent) {
    if (event.changedTouches === undefined) {
      return;
    }

    this.touchStart = event.changedTouches[0].clientX;
  }

  public onTouchEnd(event: TouchEvent) {
    if (event.changedTouches === undefined) {
      return;
    }

    this.touchEnd = event.changedTouches[0].clientX;
    const threshold = Math.abs(this.touchStart - this.touchEnd);

    // Touch moved more than 100px (left or right)
    if (threshold > 100) {
      if (this.touchEnd < this.touchStart) {
        this.scrollPrevious();
      } else {
        this.scrollNext();
      }
    }
  }

  private isEnd(): boolean {
    let bool: boolean;

    // check if end using itemCount
    // otherwise fallback to the somewhat effective but flaky hasOverflow()
    if (this.itemCount) {
      bool = ((this.position + 1) * this.scrollAmount) >= this.scrollWidth;
    } else {
      bool = !this.hasOverflow();
    }

    return bool;
  }

  private isStart(): boolean {
    return this.scrollPosition === 0;
  }

  public updateState() {
    this.animationFinished.emit(true);
    this.overflowed.emit(this.hasOverflow());

    this.scrollIsStartPromise().then(() => {
      this.scrollIsStart = this.isStart();
      this.scrollIsStartEvent.emit(this.isStart());
    });

    this.scrollIsEndPromise().then(() => {
      this.scrollIsEnd = this.isEnd();
      this.scrollIsEndEvent.emit(this.isEnd());
    });
  }

  private setCurrentScrollAmount(isPrevious?: boolean) {
    const scrollAmount = this.scrollerContainer.nativeElement.clientWidth <=320 ? 320 : this.scrollAmount;

    if (isPrevious) {
      this.scrollPosition = this.scrollPosition + scrollAmount;
    } else {
      this.scrollPosition = this.scrollPosition - scrollAmount;
    }

    this.translateXPosition = `${this.scrollPosition}px`;
  }

  private isStartPromise(): Promise<boolean> {
    const time = this.duration * 1000;
    return new Promise((resolve) => {
      setTimeout(() => resolve(this.isStart()), time);
    });
  }

  private isEndPromise(): Promise<boolean> {
    const time = this.duration * 1000;
    return new Promise((resolve) => {
      setTimeout(() => resolve(this.isEnd()), time);
    });
  }
}
